import { ReactNode, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useFluidNavigation } from '@/contexts/FluidNavigationContext';
import { useHorizontalSwipe } from '@/hooks/useHorizontalSwipe';
import Dashboard from '@/pages/Dashboard';
import MobileTasks from '@/pages/MobileTasks';
import MobileCalendar from '@/pages/MobileCalendar';
import MobilePersons from '@/pages/MobilePersons';
import MobileDocuments from '@/pages/MobileDocuments';

interface FluidNavigationContainerProps {
  children?: ReactNode;
}

export const FluidNavigationContainer = ({ children }: FluidNavigationContainerProps) => {
  const { currentIndex, navigateToIndex, pages } = useFluidNavigation();
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);

  // Page components mapping
  const pageComponents = [
    Dashboard,
    MobileTasks,
    MobileCalendar,
    MobilePersons,
    MobileDocuments
  ];

  // Handle horizontal swipe gestures
  const { handleTouchStart, handleTouchMove, handleTouchEnd } = useHorizontalSwipe({
    onSwipeLeft: () => {
      if (currentIndex < pages.length - 1) {
        navigateToIndex(currentIndex + 1);
      }
    },
    onSwipeRight: () => {
      if (currentIndex > 0) {
        navigateToIndex(currentIndex - 1);
      }
    },
    threshold: 20
  });

  // Check if we should show the fluid container or fall back to children
  const hideNavigationRoutes = [
    '/create',
    '/tasks/create',
    '/edit/',
    '/events/create',
    '/contacts/create',
    '/persons/create',
    '/task-list-settings',
    '/auth',
    '/onboarding',
    '/new-user-onboarding',
    '/invitation/',
    '/landing'
  ];
  
  const shouldShowFluidNavigation = !hideNavigationRoutes.some(route => location.pathname.includes(route));

  if (!shouldShowFluidNavigation) {
    return <>{children}</>; // Fallback to normal routing for creation/edit pages
  }

  return (
    <div 
      ref={containerRef}
      className="fluid-navigation-container"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div 
        className="fluid-navigation-wrapper"
        style={{
          transform: `translateX(-${currentIndex * 100}vw)`,
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        {pageComponents.map((PageComponent, index) => (
          <div
            key={pages[index]}
            className="page-container flex-shrink-0 h-full"
            style={{ width: '100vw' }}
          >
            <PageComponent />
          </div>
        ))}
      </div>
    </div>
  );
};